/**
 * Button component overrides: buttons, icon buttons, FABs, toggle buttons.
 * @see https://mui.com/material-ui/components/buttons/
 * @see https://mui.com/material-ui/components/button-group/
 * @see https://mui.com/material-ui/components/toggle-button/
 */
import varnishTokens from '@allenai/varnish-theme/mui';
import type { Components, Theme } from '@mui/material';

export const buttonComponents: Components<Omit<Theme, 'components'>> = {
    MuiButton: {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            root: {
                textTransform: 'none',
                lineHeight: '1',
            },
            sizeSmall: {
                padding: '10px 14px',
                fontSize: '0.75rem',
            },
            sizeMedium: {
                padding: '11px 16px',
                fontSize: varnishTokens.font.size.sm.value,
            },
            sizeLarge: {
                padding: '16px 18px',
                fontSize: varnishTokens.font.size.md.value,
            },
        },
        variants: [
            {
                props: { variant: 'outlined' },
                style: {
                    border: '1px solid',
                },
            },
            // Padding compensates for 1px border: visual size = padding + border
            {
                props: { variant: 'outlined', size: 'small' },
                style: {
                    padding: '9px 13px',
                },
            },
            {
                props: { variant: 'outlined', size: 'medium' },
                style: {
                    padding: '10px 15px',
                },
            },
            {
                props: { variant: 'outlined', size: 'large' },
                style: {
                    padding: '15px 17px',
                },
            },
            {
                props: { variant: 'contained', color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.tertiary.main,
                        color: palette.tertiary.contrastText,
                        '&:hover': { backgroundColor: palette.tertiary.hovered?.fill },
                    };
                },
            },
            {
                props: { variant: 'outlined', color: 'tertiary' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.tertiary.main,
                        borderColor: palette.tertiary.main,
                        '&:hover': {
                            borderColor: palette.tertiary.hovered?.stroke,
                        },
                    };
                },
            },
            {
                props: { variant: 'text' },
                style: ({ theme }) => ({
                    '&:hover': {
                        backgroundColor: (theme.vars ?? theme).palette.button.text.hovered.fill,
                    },
                }),
            },
            {
                props: { variant: 'text', color: 'tertiary' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.tertiary.main }),
            },
            {
                props: { variant: 'contained', color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        backgroundColor: palette.default.main,
                        color: palette.default.contrastText,
                        '&:hover': { backgroundColor: palette.default.hovered?.fill },
                    };
                },
            },
            {
                props: { variant: 'outlined', color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.default.main,
                        borderColor: palette.default.main,
                        '&:hover': {
                            borderColor: palette.default.hovered?.stroke,
                        },
                    };
                },
            },
            {
                props: { variant: 'text', color: 'default' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.default.main }),
            },
            {
                props: { shape: 'rounded' },
                style: { borderRadius: varnishTokens.radii.full.value },
            },
        ],
    },
    MuiIconButton: {
        styleOverrides: {
            root: ({ ownerState, theme }) => {
                const variant = ownerState.variant;
                const colorKey = ownerState.color || 'default';
                const themeWithVars = theme.vars ?? theme;
                const p = themeWithVars.palette[colorKey as keyof typeof themeWithVars.palette] as
                    | { main?: string; contrastText?: string; hovered?: { fill?: string } }
                    | undefined;

                return {
                    ...(['tertiary', 'default'].includes(colorKey) &&
                        (!variant || variant === 'text') && { color: p?.main }),
                    ...(variant === 'contained' &&
                        p?.main && {
                            backgroundColor: p.main,
                            color: p.contrastText,
                            '&:hover': { backgroundColor: p.hovered?.fill ?? p.main },
                        }),
                    ...(variant === 'outlined' &&
                        p?.main && {
                            borderColor: p.main,
                            color: p.main,
                        }),
                };
            },
            // padding is 1px smaller to compensate for border
            sizeSmall: {
                padding: '6px',
                fontSize: '1.25rem',
            },
            sizeMedium: {
                padding: '6px',
                fontSize: varnishTokens.sizes.icon.sm.value,
            },
            sizeLarge: {
                padding: '8px',
                fontSize: varnishTokens.sizes.icon.lg.value,
            },
        },
        variants: [
            {
                props: { variant: 'outlined', size: 'small' },
                style: {
                    padding: '5px',
                },
            },
            {
                props: { variant: 'outlined', size: 'medium' },
                style: {
                    padding: '5px',
                },
            },
            {
                props: { variant: 'outlined', size: 'large' },
                style: {
                    padding: '8px',
                },
            },
            {
                props: { shape: 'rounded' },
                style: { borderRadius: varnishTokens.radii.full.value },
            },
        ],
    },
};
